/**
 * Centralized image assets with optimized loading
 * 
 * This module provides:
 * - SVG versions for icons (much smaller than PNG)
 * - Lazy loading configuration
 * - Blur-up placeholder support
 * - CDN-friendly paths from public folder
 */

// Image assets
import logoImage from '@/assets/logo.png';
import rupeeIcon from '@/assets/rupee_icon.png';

export const IMAGES = {
  // Brand logos (original PNG)
  logo: logoImage,
  logoFallback: logoImage,
  
  // Currency icons
  hryvniaIcon: '/images/hryvnia-icon.svg',
  rupeeIcon: rupeeIcon,
  
  // Agent icon
  agriAgentIcon: '/images/agri-agent-icon.svg',
  
  // PWA icons (must stay as PNG for manifest compatibility)
  icon192: '/icon-192.png',
  icon512: '/icon-512.png',
} as const;

// Blur placeholders for common images (inline data URIs)
export const PLACEHOLDERS = {
  logo: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxMDAgMTAwIj48cmVjdCBmaWxsPSIjMjJDNTVFIiB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgcng9IjUwIi8+PC9zdmc+',
  agent: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxMDAgMTAwIj48cmVjdCBmaWxsPSIjMjJDNTVFIiB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgcng9IjUwIi8+PC9zdmc+',
  currency: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxMDAgMTAwIj48cmVjdCBmaWxsPSIjZTVlN2ViIiB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgcng9IjUwIi8+PC9zdmc+',
} as const;

// Image dimensions for proper aspect ratio hints
export const IMAGE_DIMENSIONS = {
  logo: { width: 200, height: 200 },
  logoSmall: { width: 40, height: 40 },
  agentIcon: { width: 48, height: 48 },
  currencyIcon: { width: 34, height: 34 },
} as const;

// Check if image is SVG (can skip lazy loading overhead)
export const isSvg = (src: string): boolean => {
  return src.endsWith('.svg');
};

// Get optimized loading strategy
export const getLoadingStrategy = (src: string, priority: boolean = false) => {
  if (priority) {
    return { loading: 'eager' as const, decoding: 'sync' as const };
  }
  
  // SVGs are small, can load eagerly
  if (isSvg(src)) {
    return { loading: 'eager' as const, decoding: 'async' as const };
  }
  
  return { loading: 'lazy' as const, decoding: 'async' as const };
};
